import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { useAuthStore } from "@/store/authSlice";

const BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

// ── Main API instance ─────────────────────────────────────────────────────────
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

// ── Separate instance for auth calls (no interceptors — avoids recursion) ────
const authClient = axios.create({ baseURL: BASE_URL, timeout: 10000 });

// ── Token expiry helpers ──────────────────────────────────────────────────────
function parseJwtExpiry(token: string): number {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    return payload.exp * 1000; // ms
  } catch {
    return 0;
  }
}

function isTokenExpiringSoon(token: string, bufferMs = 60_000): boolean {
  return parseJwtExpiry(token) - Date.now() < bufferMs;
}

// ── Proactive refresh — deduplicates concurrent calls ────────────────────────
let refreshPromise: Promise<string> | null = null;

async function ensureFreshToken(): Promise<string | null> {
  const { accessToken, refreshToken, setAccessToken, logout } =
    useAuthStore.getState();

  if (!accessToken) return null;

  // Token still valid — use as-is
  if (!isTokenExpiringSoon(accessToken)) return accessToken;

  if (!refreshToken) {
    logout();
    return null;
  }

  // If a refresh is already in flight, reuse that promise
  if (!refreshPromise) {
    refreshPromise = authClient
      .post<{ access_token: string }>("/auth/refresh", {
        refresh_token: refreshToken,
      })
      .then((r) => {
        setAccessToken(r.data.access_token);
        return r.data.access_token;
      })
      .catch(() => {
        logout();
        return Promise.reject(new Error("Session expired"));
      })
      .finally(() => {
        refreshPromise = null;
      });
  }

  return refreshPromise;
}

// ── Request interceptor: attach fresh token before every call ─────────────────
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Auth endpoints handle their own credentials
    if (config.url?.includes("/auth/")) return config;

    const token = await ensureFreshToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    return config;
  },
  (error) => Promise.reject(error)
);

// ── Response interceptor: fallback logout on 401 ─────────────────────────────
// Proactive refresh above handles the normal expiry case.
// This catches any unexpected 401 (revoked token, server secret rotation, etc.)
apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const url = (error.config as InternalAxiosRequestConfig)?.url ?? "";

    if (error.response?.status === 401 && !url.includes("/auth/")) {
      // Token was rejected by server even after refresh — force logout
      useAuthStore.getState().logout();
    }

    if (error.response?.status === 403) {
      console.warn("Access denied:", url);
    }

    return Promise.reject(error);
  }
);

// ── Typed API helpers ─────────────────────────────────────────────────────────
export const api = {
  get: <T>(url: string, params?: object) =>
    apiClient.get<T>(url, { params }).then((r) => r.data),

  post: <T>(url: string, data?: object, options?: { timeout?: number }) =>
    apiClient
      .post<T>(url, data, { timeout: options?.timeout })
      .then((r) => r.data),

  put: <T>(url: string, data?: object, options?: { timeout?: number }) =>
    apiClient
      .put<T>(url, data, { timeout: options?.timeout })
      .then((r) => r.data),

  patch: <T>(url: string, data?: object) =>
    apiClient.patch<T>(url, data).then((r) => r.data),

  delete: <T>(url: string) => apiClient.delete<T>(url).then((r) => r.data),

  upload: <T>(
    url: string,
    formData: FormData,
    options?: {
      timeout?: number;
      onUploadProgress?: (e: { loaded: number; total?: number }) => void;
    }
  ) =>
    apiClient
      .post<T>(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: options?.timeout ?? 900_000,
        onUploadProgress: options?.onUploadProgress,
      })
      .then((r) => r.data),
};

export default apiClient;
